import { router } from "@inertiajs/react";
import { LoaderIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatCurrency } from "@/lib/utils";
import type { IncentiveSummary } from "@/types/admin-portal/bonus-ro";
import { payoutPrefill, periodInvalid } from "./payouts";
import { parseAmount, sendWrite } from "./rules";

/**
 * Drafts a payout for one therapist. Nothing leaves the balance yet: it drops when the
 * payout is marked paid. The amount is not capped at the balance; the backend allows more.
 */
export function PayoutDialog({
	therapist,
	url,
	onClose,
}: {
	therapist: IncentiveSummary;
	url: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("bonus-ro");
	const [amountText, setAmountText] = useState(() =>
		payoutPrefill(therapist.balance),
	);
	const [periodStart, setPeriodStart] = useState("");
	const [periodEnd, setPeriodEnd] = useState("");
	const [notes, setNotes] = useState("");
	const [saving, setSaving] = useState(false);

	const amount = parseAmount(amountText);
	const amountInvalid = amountText !== "" && !amount;
	const periodWrong = periodInvalid(periodStart, periodEnd);
	const canSave = !!amount && !periodWrong && !saving;
	const name = therapist.therapistName ?? therapist.therapistId;
	const balanceLeft = amount ? therapist.balance - amount : null;

	async function onSave() {
		setSaving(true);
		const result = await sendWrite(
			url,
			"POST",
			{
				therapist_id: therapist.therapistId,
				amount,
				period_start: periodStart,
				period_end: periodEnd,
				notes: notes.trim(),
			},
			t("payout_create.error"),
		);
		setSaving(false);
		if (!result.ok) {
			toast.error(result.error);
			return;
		}
		toast.success(t("payout_create.saved", { name }));
		onClose();
		router.get(window.location.pathname, { tab: "payouts" });
	}

	return (
		<Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("payout_create.title", { name })}</DialogTitle>
					<DialogDescription>
						{t("payout_create.description")}
					</DialogDescription>
				</DialogHeader>

				<div className="flex items-baseline justify-between gap-4 border px-3 py-2">
					<span className="text-sm text-muted-foreground">
						{t("therapists.columns.balance")}
					</span>
					<span
						className={cn(
							"font-semibold tabular-nums",
							therapist.balance < 0 && "text-destructive",
						)}
					>
						{formatCurrency(therapist.balance)}
					</span>
				</div>

				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label htmlFor="bonus-ro-payout-amount">
							{t("payout_create.amount")}
						</Label>
						<Input
							className="bg-input shadow-inner"
							id="bonus-ro-payout-amount"
							inputMode="numeric"
							value={amountText}
							placeholder="250000"
							aria-invalid={amountInvalid}
							onChange={(e) => setAmountText(e.target.value)}
						/>
						<p
							className={cn(
								"text-xs text-muted-foreground",
								(amountInvalid || (balanceLeft ?? 0) < 0) && "text-destructive",
							)}
						>
							{amount && balanceLeft !== null
								? t(
										balanceLeft < 0
											? "payout_create.amount_over"
											: "payout_create.amount_left",
										{
											amount: formatCurrency(amount),
											left: formatCurrency(balanceLeft),
										},
									)
								: t("payout_create.amount_hint")}
						</p>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="flex flex-col gap-2">
							<Label htmlFor="bonus-ro-payout-start">
								{t("payout_create.period_start")}
							</Label>
							<Input
								className="bg-input shadow-inner"
								id="bonus-ro-payout-start"
								type="date"
								value={periodStart}
								max={periodEnd || undefined}
								onChange={(e) => setPeriodStart(e.target.value)}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="bonus-ro-payout-end">
								{t("payout_create.period_end")}
							</Label>
							<Input
								className="bg-input shadow-inner"
								id="bonus-ro-payout-end"
								type="date"
								value={periodEnd}
								min={periodStart || undefined}
								aria-invalid={periodWrong}
								onChange={(e) => setPeriodEnd(e.target.value)}
							/>
						</div>
						<p
							className={
								periodWrong
									? "text-xs text-destructive sm:col-span-2"
									: "text-xs text-muted-foreground sm:col-span-2"
							}
						>
							{periodWrong
								? t("payout_create.period_invalid")
								: t("payout_create.period_hint")}
						</p>
					</div>

					<div className="flex flex-col gap-2">
						<Label htmlFor="bonus-ro-payout-notes">
							{t("payout_create.notes")}
						</Label>
						<Textarea
							className="bg-input shadow-inner"
							id="bonus-ro-payout-notes"
							value={notes}
							rows={2}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.cancel")}
					</Button>
					<Button onClick={onSave} disabled={!canSave}>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t("payout_create.submit")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
